import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Search, Upload, RefreshCw, Filter, Eye, Database, TrendingUp, AlertTriangle, PieChart } from 'lucide-react';

const SecurityMaster = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('securities');
  const [securities, setSecurities] = useState([]);
  const [modelPortfolios, setModelPortfolios] = useState([]);
  const [restrictedAssets, setRestrictedAssets] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAssetClass, setSelectedAssetClass] = useState('All');
  const [selectedSecurity, setSelectedSecurity] = useState(null);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const assetClasses = ['All', 'Equity', 'Mutual Fund', 'AIF', 'Fixed Income', 'Commodity', 'International'];

  useEffect(() => {
    if (activeTab === 'securities') {
      fetchSecurities();
    } else if (activeTab === 'models') {
      fetchModelPortfolios();
    } else if (activeTab === 'restricted') {
      fetchRestrictedAssets();
    } else if (activeTab === 'recommendations') {
      fetchRecommendations();
    }
  }, [selectedAssetClass, activeTab]);

  const fetchSecurities = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();

      if (selectedAssetClass !== 'All') {
        params.append('asset_class', selectedAssetClass);
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/security-master?${params.toString()}`,
        {
          credentials: 'include',
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSecurities(data.securities || []);
        setTotalCount(data.total_count || 0);
      }
    } catch (error) {
      console.error('Error fetching securities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      fetchSecurities();
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('search', searchQuery);

      if (selectedAssetClass !== 'All') {
        params.append('asset_class', selectedAssetClass);
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/security-master?${params.toString()}`,
        {
          credentials: 'include',
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSecurities(data.securities || []);
        setTotalCount(data.total_count || 0);
      }
    } catch (error) {
      console.error('Error searching securities:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchModelPortfolios = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/security-master/model-portfolios`,
        {
          credentials: 'include',
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setModelPortfolios(data.model_portfolios || []);
      }
    } catch (error) {
      console.error('Error fetching model portfolios:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRestrictedAssets = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/security-master/restricted-assets`,
        {
          credentials: 'include',
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setRestrictedAssets(data);
      }
    } catch (error) {
      console.error('Error fetching restricted assets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommendations = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/recommendations`,
        {
          credentials: 'include',
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setRecommendations(data);
      }
    } catch (error) {
      console.error('Error fetching recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/security-master/sync`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );
      
      if (response.ok) {
        alert('Security master synced successfully!');
        fetchSecurities();
      }
    } catch (error) {
      console.error('Error syncing security master:', error);
      alert('Failed to sync security master');
    } finally {
      setSyncing(false);
    }
  };

  const viewSecurityDetail = (security) => {
    setSelectedSecurity(security);
    setShowDetailModal(true);
  };

  const getRiskBadgeColor = (rating) => {
    const r = (rating || '').toLowerCase();
    if (r === 'very low') return 'bg-green-100 text-green-800';
    if (r === 'low') return 'bg-green-200 text-green-900';
    if (r === 'low-moderate' || r === 'low moderate') return 'bg-teal-100 text-teal-800';
    if (r === 'moderate') return 'bg-yellow-100 text-yellow-800';
    if (r === 'moderate-high' || r === 'moderate high') return 'bg-orange-100 text-orange-800';
    if (r === 'high') return 'bg-red-100 text-red-800';
    if (r === 'very high') return 'bg-red-200 text-red-900';
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{fontFamily: "'PerfectNineties', serif"}}>
              Security Master
            </h1>
            <p className="text-gray-600">
              Investment products, model portfolios & restrictions
            </p>
          </div>
          
          {/* Sync Action */}
          {activeTab === 'securities' && (
            <Button 
              onClick={handleSync}
              disabled={syncing}
              className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black"
            >
              {syncing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Sync Data
                </>
              )}
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('securities')}
              className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'securities'
                  ? 'border-[#ffd87a] text-ugmonk-dark'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Database className="w-4 h-4 inline mr-2" />
              All Securities
            </button>
            <button
              onClick={() => setActiveTab('models')}
              className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'models'
                  ? 'border-[#ffd87a] text-ugmonk-dark'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <PieChart className="w-4 h-4 inline mr-2" />
              Model Portfolios
            </button>
            <button
              onClick={() => setActiveTab('restricted')}
              className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'restricted'
                  ? 'border-[#ffd87a] text-ugmonk-dark'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <AlertTriangle className="w-4 h-4 inline mr-2" />
              Restricted Assets
            </button>
            <button
              onClick={() => setActiveTab('recommendations')}
              className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'recommendations'
                  ? 'border-[#ffd87a] text-ugmonk-dark'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <TrendingUp className="w-4 h-4 inline mr-2" />
              Recommendations
            </button>
          </div>
        </div>

        {/* Securities Tab Content */}
        {activeTab === 'securities' && (
          <>
            {/* Search and Filter Controls */}
        <Card className="border-gray-200 bg-white">
          <CardContent className="pt-6 bg-white">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Search */}
              <div className="md:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-3.5 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search by name, ISIN, or symbol..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="w-full pl-10 pr-24 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ffd87a] focus:border-transparent"
                  />
                  <button
                    onClick={handleSearch}
                    className="absolute right-2 top-2 px-4 py-1.5 bg-[#ffd87a] text-black rounded-md hover:bg-[#f5cc6a] transition-colors text-sm font-medium"
                  >
                    Search
                  </button>
                </div>
              </div>

              {/* Results Count */}
              <div className="flex items-center justify-center md:justify-end px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
                <Database className="w-4 h-4 text-gray-600 mr-2" />
                <span className="text-sm text-gray-700">
                  <span className="font-semibold">{securities.length}</span> of{' '}
                  <span className="font-semibold">{totalCount}</span> securities
                </span>
              </div>
            </div>

            {/* Asset Class Filter */}
            <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-gray-200">
              <Filter size={18} className="text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Asset Class:</span>
              {assetClasses.map((assetClass) => (
                <button
                  key={assetClass}
                  onClick={() => setSelectedAssetClass(assetClass)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    selectedAssetClass === assetClass
                      ? 'bg-[#ffd87a] text-black'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {assetClass}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Securities Table */}
        <Card className="border-gray-200 bg-white">
          <CardHeader className="pb-3 bg-white">
            <CardTitle className="text-gray-900 flex items-center gap-2">
              <Database className="w-5 h-5 text-ugmonk-dark" />
              Investment Securities
            </CardTitle>
          </CardHeader>
          <CardContent className="bg-white">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <RefreshCw className="animate-spin text-ugmonk-dark" size={40} />
              </div>
            ) : securities.length === 0 ? (
              <div className="text-center py-20 text-gray-500">
                <Database className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg mb-2">No securities found</p>
                <p className="text-sm">Try adjusting your search or filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Security Name
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Symbol / ISIN
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Asset Class
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Sub-Asset Class
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Benchmark
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Risk Rating
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {securities.map((security, index) => (
                      <tr
                        key={security.security_id || index}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900">{security.security_name}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{security.symbol}</div>
                          <div className="text-xs text-gray-500">{security.isin}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-[#fffdf0] text-ugmonk-dark">
                            {security.asset_class}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {security.sub_asset_class}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {security.benchmark}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${getRiskBadgeColor(security.risk_rating)}`}>
                            {security.risk_rating}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => viewSecurityDetail(security)}
                            className="text-ugmonk-dark hover:text-[#f5cc6a] flex items-center gap-1 text-sm font-medium"
                          >
                            <Eye size={16} />
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
          </>
        )}

        {/* Model Portfolios Tab Content */}
        {activeTab === 'models' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {loading ? (
              <div className="col-span-3 flex items-center justify-center py-20">
                <RefreshCw className="animate-spin text-ugmonk-dark" size={40} />
              </div>
            ) : (
              modelPortfolios.map((portfolio) => (
                <Card key={portfolio.id} className="border-gray-200 hover:shadow-lg transition-shadow bg-white">
                  <CardHeader className="pb-3 bg-white">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg text-gray-900 mb-2">
                          {portfolio.name}
                        </CardTitle>
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                          portfolio.risk_profile === 'Conservative' ? 'bg-green-100 text-green-800' :
                          portfolio.risk_profile === 'Balanced' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-[#fffdf0] text-ugmonk-darker'
                        }`}>
                          {portfolio.risk_profile}
                        </span>
                      </div>
                      <TrendingUp className="w-5 h-5 text-ugmonk-dark" />
                    </div>
                  </CardHeader>
                  <CardContent className="bg-white">
                    <p className="text-sm text-gray-600 mb-4">{portfolio.description}</p>
                    
                    <div className="space-y-3 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Target Return:</span>
                        <span className="font-semibold text-gray-900">{portfolio.target_return}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Volatility:</span>
                        <span className="font-semibold text-gray-900">{portfolio.volatility}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Min Investment:</span>
                        <span className="font-semibold text-gray-900">
                          ₹{(portfolio.minimum_investment / 10000000).toFixed(1)}Cr
                        </span>
                      </div>
                    </div>

                    <div className="mb-4">
                      <p className="text-xs font-semibold text-gray-700 mb-2">Asset Allocation:</p>
                      <div className="space-y-1">
                        {Object.entries(portfolio.asset_allocation).map(([asset, percentage]) => (
                          percentage > 0 && (
                            <div key={asset} className="flex items-center gap-2">
                              <div className="flex-1">
                                <div className="flex justify-between text-xs mb-1">
                                  <span className="text-gray-600">{asset}</span>
                                  <span className="font-medium">{percentage}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-1.5">
                                  <div 
                                    className="bg-[#1a1a1a] h-1.5 rounded-full" 
                                    style={{width: `${percentage}%`}}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          )
                        ))}
                      </div>
                    </div>

                    <Button
                      onClick={() => {
                        setSelectedPortfolio(portfolio);
                        setShowPortfolioModal(true);
                      }}
                      className="w-full bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black"
                    >
                      View Holdings
                    </Button>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Restricted Assets Tab Content */}
        {activeTab === 'restricted' && restrictedAssets && (
          <div className="space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <RefreshCw className="animate-spin text-ugmonk-dark" size={40} />
              </div>
            ) : (
              <>
                {/* Restricted Asset Classes */}
                <Card className="border-gray-200 bg-white">
                  <CardHeader className="pb-3 bg-white">
                    <CardTitle className="text-gray-900 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      Restricted Asset Classes
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="bg-white">
                    <div className="overflow-x-auto">
                      <table className="w-full bg-white">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Asset Class</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Reason</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Restriction Level</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Effective Date</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {restrictedAssets.restricted_asset_classes.map((item, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3 font-medium text-gray-900">{item.asset_class}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{item.reason}</td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                                  item.restriction_level.includes('Complete') 
                                    ? 'bg-red-100 text-red-800' 
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {item.restriction_level}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">{item.effective_date}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Restricted Sectors */}
                <Card className="border-gray-200 bg-white">
                  <CardHeader className="pb-3 bg-white">
                    <CardTitle className="text-gray-900 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-ugmonk-dark" />
                      Restricted Sectors (ESG)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="bg-white">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {restrictedAssets.restricted_sectors.map((item, idx) => (
                        <div key={idx} className="p-4 border border-red-200 rounded-lg bg-red-50">
                          <h4 className="font-semibold text-gray-900 mb-2">{item.sector}</h4>
                          <p className="text-sm text-gray-600 mb-2">{item.reason}</p>
                          <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {item.restriction_level}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Restricted Securities */}
                <Card className="border-gray-200 bg-white">
                  <CardHeader className="pb-3 bg-white">
                    <CardTitle className="text-gray-900 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      Restricted Securities
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="bg-white">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Security</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">ISIN</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Reason</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {restrictedAssets.restricted_securities.map((item, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3 font-medium text-gray-900">{item.security_name}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{item.isin}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{item.reason}</td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                                  item.restriction_level === 'Complete Ban' 
                                    ? 'bg-red-100 text-red-800' 
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {item.restriction_level}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Investment Guidelines */}
                <Card className="border-gray-200 bg-white">
                  <CardHeader className="pb-3 bg-white">
                    <CardTitle className="text-gray-900 flex items-center gap-2">
                      <Database className="w-5 h-5 text-ugmonk-dark" />
                      Investment Guidelines
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="bg-white">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {restrictedAssets.investment_guidelines.map((item, idx) => (
                        <div key={idx} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                          <div className="flex items-start gap-2">
                            <div className="w-2 h-2 bg-[#ffd87a] rounded-full mt-2"></div>
                            <div>
                              <p className="font-medium text-gray-900 mb-1">{item.guideline}</p>
                              <p className="text-xs text-gray-600">
                                <span className="font-semibold">{item.category}</span> · {item.applicable_to}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}

        {/* Recommendations Tab Content (View Only for RM) */}
        {activeTab === 'recommendations' && (
          <div className="mt-6">
            <Card className="border-gray-200 bg-white">
              <CardHeader className="pb-3 bg-white">
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-ugmonk-dark" />
                  Product Recommendations (View Only)
                </CardTitle>
              </CardHeader>
              <CardContent className="bg-white">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a1a1a]"></div>
                  </div>
                ) : recommendations.length === 0 ? (
                  <div className="text-center py-12">
                    <TrendingUp className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-semibold text-gray-900 mb-2">No Recommendations Available</p>
                    <p className="text-gray-600">No recommendations have been added yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b-2 border-gray-200 bg-gray-50">
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Product</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Type</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">ISIN</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Recommendation</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Target %</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Risk Profiles</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Rationale</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {recommendations.map((rec, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium text-gray-900">{rec.product_name}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{rec.product_type}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 font-mono">{rec.isin || '-'}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                                rec.recommendation_type === 'Buy' 
                                  ? 'bg-green-100 text-green-800' 
                                  : rec.recommendation_type === 'Sell'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {rec.recommendation_type}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 font-semibold">{rec.target_allocation}%</td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              <div className="flex flex-wrap gap-1">
                                {rec.risk_profile_names?.map((profile, i) => (
                                  <span key={i} className="inline-flex px-2 py-0.5 bg-[#fffdf0] text-ugmonk-dark rounded text-xs">
                                    {profile}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate" title={rec.rationale}>
                              {rec.rationale}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Security Detail Modal */}
      {showDetailModal && selectedSecurity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Security Details</h2>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-semibold text-gray-600">Security Name</label>
                  <p className="text-lg text-gray-900">{selectedSecurity.security_name}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Symbol</label>
                    <p className="text-gray-900">{selectedSecurity.symbol}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">ISIN</label>
                    <p className="text-gray-900">{selectedSecurity.isin}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Asset Class</label>
                    <p className="text-gray-900">{selectedSecurity.asset_class}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Sub-Asset Class</label>
                    <p className="text-gray-900">{selectedSecurity.sub_asset_class}</p>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-600">Benchmark</label>
                  <p className="text-gray-900">{selectedSecurity.benchmark}</p>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-600">Security Type</label>
                  <p className="text-gray-900">{selectedSecurity.security_type}</p>
                </div>

                {selectedSecurity.sector && (
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Sector</label>
                    <p className="text-gray-900">{selectedSecurity.sector}</p>
                  </div>
                )}

                {selectedSecurity.exchange && (
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Exchange</label>
                    <p className="text-gray-900">{selectedSecurity.exchange}</p>
                  </div>
                )}

                {selectedSecurity.amc_name && (
                  <div>
                    <label className="text-sm font-semibold text-gray-600">AMC Name</label>
                    <p className="text-gray-900">{selectedSecurity.amc_name}</p>
                  </div>
                )}

                {selectedSecurity.expense_ratio && (
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Expense Ratio</label>
                    <p className="text-gray-900">{selectedSecurity.expense_ratio}</p>
                  </div>
                )}

                <div>
                  <label className="text-sm font-semibold text-gray-600">Risk Rating</label>
                  <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${getRiskBadgeColor(selectedSecurity.risk_rating)}`}>
                    {selectedSecurity.risk_rating}
                  </span>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-600">Status</label>
                  <span className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {selectedSecurity.status}
                  </span>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Portfolio Detail Modal */}
      {showPortfolioModal && selectedPortfolio && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedPortfolio.name}</h2>
                  <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                    selectedPortfolio.risk_profile === 'Conservative' ? 'bg-green-100 text-green-800' :
                    selectedPortfolio.risk_profile === 'Balanced' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-[#fffdf0] text-ugmonk-darker'
                  }`}>
                    {selectedPortfolio.risk_profile}
                  </span>
                </div>
                <button
                  onClick={() => setShowPortfolioModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Target Return</label>
                    <p className="text-lg font-bold text-ugmonk-dark">{selectedPortfolio.target_return}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Volatility</label>
                    <p className="text-lg font-bold text-gray-900">{selectedPortfolio.volatility}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Rebalancing</label>
                    <p className="text-lg font-bold text-gray-900">{selectedPortfolio.rebalancing_frequency}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Minimum Investment</label>
                    <p className="text-lg font-bold text-gray-900">
                      ₹{(selectedPortfolio.minimum_investment / 10000000).toFixed(1)}Cr
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Portfolio Holdings</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Security</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Asset Class</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700">Allocation %</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Rationale</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedPortfolio.holdings.map((holding, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900">{holding.security_name}</div>
                              <div className="text-xs text-gray-500">{holding.security_id}</div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-[#fffdf0] text-ugmonk-dark">
                                {holding.asset_class}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="font-semibold text-ugmonk-dark">{holding.allocation_percentage}%</span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{holding.rationale}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowPortfolioModal(false)}
                  className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default SecurityMaster;
